import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./PlaceOrder.scss";
import { showToast } from "../../store/reducers/toasterReducer";
import { OrderService } from "../../utils/api.services";
import { getStoreInfo } from "../../utils/utils";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { hideSpinner, showSpinner } from "../../store/reducers/spinnerSlice";
import MobileAuthModal from "./components/MobileAuthModal";
import ActiveOrderScreen from "./components/ActiveOrderScreen";

const PlaceOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { menuItems, restaurantInfo, tables } = useSelector(
    (state) => state.data
  );

  const scannedSessionTable =
    sessionStorage.getItem("scannedTableId") ||
    localStorage.getItem("scannedTableId");

  const effectiveId = id && id !== "0" ? id : scannedSessionTable || "0";

  const matchedTable = (tables || []).find(
    (t) =>
      String(t._id) === String(effectiveId) ||
      String(t.tableNumber) === String(effectiveId)
  );

  const queryParams = new URLSearchParams(window.location.search);
  const explicitTable =
    queryParams.get("table") ||
    matchedTable?.tableNumber ||
    matchedTable?.name ||
    (effectiveId && effectiveId !== "0" && !isNaN(effectiveId) ? effectiveId : null);

  useEffect(() => {
    if (effectiveId === "0" && !queryParams.get("table")) {
      localStorage.removeItem("tableNumber");
    } else if (explicitTable) {
      localStorage.setItem("tableNumber", explicitTable);
    }
  }, [effectiveId, explicitTable]);

  const tableParam =
    explicitTable ||
    (effectiveId !== "0" ? localStorage.getItem("tableNumber") : null);

  const isParcelQR = Boolean(
    matchedTable?.type === "PARCEL" ||
    String(matchedTable?.tableNumber || "").toUpperCase().includes("PARCEL") ||
    String(queryParams.get("table") || "").toUpperCase().includes("PARCEL") ||
    String(tableParam || "").toUpperCase().includes("PARCEL")
  );

  const incomingCartDineIn = location.state?.cartDineIn;
  const incomingCartPickUp = location.state?.cartPickUp;
  const incomingSelectedTab = location.state?.selectedTab;

  // Initialize carts from location state or fallback to persisted landing cart
  const [cartDineIn, setCartDineIn] = useState(() => {
    if (incomingCartDineIn && Object.keys(incomingCartDineIn).length > 0) {
      return incomingCartDineIn;
    }
    if (isParcelQR) return {};
    try {
      const saved = sessionStorage.getItem("customerCartDineIn");
      if (saved) return JSON.parse(saved);
      const legacy = sessionStorage.getItem("customerLandingCart");
      return legacy ? JSON.parse(legacy) : {};
    } catch {
      return {};
    }
  });

  const [cartPickUp, setCartPickUp] = useState(() => {
    if (incomingCartPickUp && Object.keys(incomingCartPickUp).length > 0) {
      return incomingCartPickUp;
    }
    try {
      const saved = sessionStorage.getItem("customerCartPickUp");
      if (saved) return JSON.parse(saved);
      if (isParcelQR) {
        const legacy = sessionStorage.getItem("customerLandingCart");
        return legacy ? JSON.parse(legacy) : {};
      }
      return {};
    } catch {
      return {};
    }
  });

  const [selectedTab, setSelectedTab] = useState(() => {
    if (incomingSelectedTab) return incomingSelectedTab;
    const saved = sessionStorage.getItem("customerActiveTab");
    if (saved && !isParcelQR) return saved;
    return isParcelQR ? "Pick Up" : "Dine In";
  });
  const [cookingInstructions, setCookingInstructions] = useState("");
  const [editingNoteItemId, setEditingNoteItemId] = useState(null);

  // Sync back to sessionStorage so return-to-menu retains state
  useEffect(() => {
    try {
      sessionStorage.setItem("customerCartDineIn", JSON.stringify(cartDineIn));
      sessionStorage.setItem("customerCartPickUp", JSON.stringify(cartPickUp));
      sessionStorage.setItem("customerActiveTab", selectedTab);
      const merged = { ...cartDineIn, ...cartPickUp };
      sessionStorage.setItem("customerLandingCart", JSON.stringify(merged));
    } catch {}
  }, [cartDineIn, cartPickUp, selectedTab]);

  // Mobile number state & storage with 24-hour expiration check
  const [mobileNumber, setMobileNumber] = useState(() => {
    const saved =
      localStorage.getItem("customerMobile") ||
      sessionStorage.getItem("customerMobile");
    const savedTimestamp =
      localStorage.getItem("customerMobileTimestamp") ||
      sessionStorage.getItem("customerMobileTimestamp");
    const isExpired =
      !savedTimestamp ||
      Date.now() - Number(savedTimestamp) > 24 * 60 * 60 * 1000;

    if (isExpired && saved) {
      localStorage.removeItem("customerMobile");
      sessionStorage.removeItem("customerMobile");
      localStorage.removeItem("customerMobileTimestamp");
      sessionStorage.removeItem("customerMobileTimestamp");
      localStorage.removeItem("currentOrder");
      localStorage.removeItem("currentOrderToken");
      return "";
    }
    return saved || "";
  });

  // Active Order & Editing state
  const [activeOrder, setActiveOrder] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [showMobileModal, setShowMobileModal] = useState(() => !mobileNumber);

  const storeInfo = restaurantInfo || getStoreInfo();
  const storeId = storeInfo?._id;

  useEffect(() => {
    if (isParcelQR) {
      setSelectedTab("Pick Up");
    }
  }, [isParcelQR]);

  useEffect(() => {
    const saved = localStorage.getItem("customerMobile") || sessionStorage.getItem("customerMobile");
    const savedTimestamp = localStorage.getItem("customerMobileTimestamp") || sessionStorage.getItem("customerMobileTimestamp");
    const isExpired = !savedTimestamp || (Date.now() - Number(savedTimestamp) > 24 * 60 * 60 * 1000);
    if (isExpired && saved) {
      localStorage.removeItem("customerMobile");
      sessionStorage.removeItem("customerMobile");
      localStorage.removeItem("customerMobileTimestamp");
      sessionStorage.removeItem("customerMobileTimestamp");
      localStorage.removeItem("currentOrder");
      localStorage.removeItem("currentOrderToken");
      setMobileNumber("");
      setShowMobileModal(true);
      return;
    }
    if (!saved) {
      setShowMobileModal(true);
    }
  }, []);

  useEffect(() => {
    if (matchedTable?.tableNumber || matchedTable?.name) {
      localStorage.setItem("tableNumber", matchedTable.tableNumber || matchedTable.name);
    }
  }, [matchedTable]);

  // Fetch active order for current mobile number
  const checkActiveOrder = useCallback(async (mobile) => {
    if (!storeId || !mobile) return;
    try {
      const res = await OrderService.getActiveOrderByMobile(storeId, mobile);
      if (res?.data?.activeOrder) {
        setActiveOrder(res.data.activeOrder);
        localStorage.setItem("currentOrder", res.data.activeOrder._id);
        localStorage.setItem(
          "currentOrderToken",
          res.data.activeOrder.token || ""
        );
      } else {
        const hadStoredOrder = localStorage.getItem("currentOrder");
        setActiveOrder(null);
        localStorage.removeItem("currentOrder");
        localStorage.removeItem("currentOrderToken");
        if (hadStoredOrder) {
          localStorage.removeItem("customerMobile");
          localStorage.removeItem("customerMobileTimestamp");
          sessionStorage.removeItem("customerMobile");
          sessionStorage.removeItem("customerMobileTimestamp");
          setMobileNumber("");
          setShowMobileModal(true);
        }
      }
    } catch (err) {
      console.error("Error checking active order:", err);
    }
  }, [storeId]);

  useEffect(() => {
    if (mobileNumber && storeId) {
      checkActiveOrder(mobileNumber);

      const pollMs = activeOrder ? 3500 : 10000;
      const interval = setInterval(() => {
        checkActiveOrder(mobileNumber);
      }, pollMs);

      return () => clearInterval(interval);
    }
  }, [mobileNumber, storeId, Boolean(activeOrder), checkActiveOrder]);

  const handleMobileAuthSubmit = (mobile) => {
    localStorage.setItem("customerMobile", mobile);
    sessionStorage.setItem("customerMobile", mobile);
    localStorage.setItem("customerMobileTimestamp", String(Date.now()));
    sessionStorage.setItem("customerMobileTimestamp", String(Date.now()));
    setMobileNumber(mobile);
    setShowMobileModal(false);
    checkActiveOrder(mobile);
  };

  const getItemDetails = (itemId) => {
    const found = (menuItems || []).find((m) => m._id === itemId);
    return found || null;
  };

  const updateCartQuantity = (cartType, itemId, delta) => {
    const targetCart = cartType === "Dine In" ? cartDineIn : cartPickUp;
    const setCart = cartType === "Dine In" ? setCartDineIn : setCartPickUp;

    const currentItem = targetCart[itemId];
    if (!currentItem) return;

    const newQty = (currentItem.quantity || 0) + delta;
    const updated = { ...targetCart };

    if (newQty <= 0) {
      delete updated[itemId];
    } else {
      updated[itemId] = {
        ...currentItem,
        quantity: newQty,
      };
    }
    setCart(updated);
  };

  const removeCartItem = (cartType, itemId) => {
    const targetCart = cartType === "Dine In" ? cartDineIn : cartPickUp;
    const setCart = cartType === "Dine In" ? setCartDineIn : setCartPickUp;
    const updated = { ...targetCart };
    delete updated[itemId];
    setCart(updated);
  };

  const updateItemNote = (cartType, itemId, note) => {
    const targetCart = cartType === "Dine In" ? cartDineIn : cartPickUp;
    const setCart = cartType === "Dine In" ? setCartDineIn : setCartPickUp;
    const currentItem = targetCart[itemId];
    if (!currentItem) return;
    setCart({
      ...targetCart,
      [itemId]: {
        ...currentItem,
        notes: (note || "").trim(),
      },
    });
  };

  const handleStartEditOrder = (orderToEdit) => {
    const dineInMap = {};
    const pickUpMap = {};

    (orderToEdit.cartItems || []).forEach((item) => {
      const id = item.itemId?._id || item.itemId || item._id;
      dineInMap[id] = {
        quantity: item.quantity,
        price: item.price,
        name: item.itemId?.name || item.name,
        image: item.itemId?.image || item.image,
        notes: item.notes || "",
      };
    });

    (orderToEdit.pickUpCartItems || []).forEach((item) => {
      const id = item.itemId?._id || item.itemId || item._id;
      pickUpMap[id] = {
        quantity: item.quantity,
        price: item.price,
        name: item.itemId?.name || item.name,
        image: item.itemId?.image || item.image,
        notes: item.notes || "",
      };
    });

    setCartDineIn(dineInMap);
    setCartPickUp(pickUpMap);
    setCookingInstructions(orderToEdit.cookingInstructions || "");
    setIsEditing(true);
    setEditingOrderId(orderToEdit._id);
  };

  const dineInCartEntries = useMemo(
    () => Object.entries(cartDineIn).filter(([_, it]) => it && it.quantity > 0),
    [cartDineIn]
  );

  const pickUpCartEntries = useMemo(
    () => Object.entries(cartPickUp).filter(([_, it]) => it && it.quantity > 0),
    [cartPickUp]
  );

  const dineInTotal = useMemo(
    () =>
      dineInCartEntries.reduce(
        (sum, [_, it]) => sum + (it.quantity || 0) * (it.price || 0),
        0
      ),
    [dineInCartEntries]
  );

  const pickUpTotal = useMemo(
    () =>
      pickUpCartEntries.reduce(
        (sum, [_, it]) => sum + (it.quantity || 0) * (it.price || 0),
        0
      ),
    [pickUpCartEntries]
  );

  const grandTotal = dineInTotal + pickUpTotal;
  const totalItemCount =
    dineInCartEntries.reduce((s, [_, it]) => s + it.quantity, 0) +
    pickUpCartEntries.reduce((s, [_, it]) => s + it.quantity, 0);

  const handleOrder = async () => {
    dispatch(showSpinner());

    if (!mobileNumber.trim()) {
      setShowMobileModal(true);
      dispatch(hideSpinner());
      return;
    }

    const dineInCartItems = dineInCartEntries.map(([itemId, item]) => ({
      itemId,
      name: item.name || item.dishName || "",
      quantity: item.quantity,
      price: item.price,
      notes: item.notes || "",
    }));

    const pickUpCartItems = pickUpCartEntries.map(([itemId, item]) => ({
      itemId,
      name: item.name || item.dishName || "",
      quantity: item.quantity,
      price: item.price,
      notes: item.notes || "",
    }));

    if (dineInCartItems.length === 0 && pickUpCartItems.length === 0) {
      dispatch(showToast({ message: "Your cart is empty.", type: "error" }));
      dispatch(hideSpinner());
      return;
    }

    try {
      if (!storeId) {
        dispatch(
          showToast({ message: "Restaurant ID not found!", type: "error" })
        );
        dispatch(hideSpinner());
        return;
      }

      if (isEditing && editingOrderId) {
        // Customer Updating Existing Order
        const effectivePickUpCartItems = isParcelQR
          ? pickUpCartItems.length > 0
            ? pickUpCartItems
            : dineInCartItems
          : pickUpCartItems;
        const effectiveDineInCartItems = isParcelQR ? [] : dineInCartItems;
        const effectiveDineInTotal = isParcelQR ? 0 : dineInTotal;
        const effectivePickUpTotal = isParcelQR ? grandTotal : pickUpTotal;

        const response = await OrderService.editOrderCustomer(editingOrderId, {
          cartItems: effectiveDineInCartItems,
          pickUpCartItems: effectivePickUpCartItems,
          totalAmount: grandTotal,
          dineInTotal: effectiveDineInTotal,
          pickUpTotal: effectivePickUpTotal,
          cookingInstructions: cookingInstructions.trim() || undefined,
        });

        if (response.status === 200) {
          dispatch(
            showToast({ message: "Order updated successfully!", type: "success" })
          );
          setActiveOrder(response.data.order);
          setIsEditing(false);
          setEditingOrderId(null);
          setCartDineIn({});
          setCartPickUp({});
          sessionStorage.removeItem("customerLandingCart");
        } else {
          throw new Error(response.data?.message || "Failed to update order");
        }
      } else {
        // Customer Placing New Order
        let dynamicOrderType = "DINE_IN";
        if (isParcelQR || (pickUpCartItems.length > 0 && dineInCartItems.length === 0)) {
          dynamicOrderType = "PARCEL";
        } else if (tableParam === "PARCEL") {
          dynamicOrderType = "PARCEL";
        } else {
          dynamicOrderType = "DINE_IN";
        }

        const effectivePickUpCartItems = isParcelQR
          ? pickUpCartItems.length > 0
            ? pickUpCartItems
            : dineInCartItems
          : pickUpCartItems;
        const effectiveDineInCartItems = isParcelQR ? [] : dineInCartItems;
        const effectiveDineInTotal = isParcelQR ? 0 : dineInTotal;
        const effectivePickUpTotal = isParcelQR ? grandTotal : pickUpTotal;

        const orderData = {
          customerDetails: {
            mobileNumber: mobileNumber.trim(),
            name: "",
            email: "",
          },
          cartItems: effectiveDineInCartItems,
          pickUpCartItems: effectivePickUpCartItems,
          totalAmount: grandTotal,
          dineInTotal: effectiveDineInTotal,
          pickUpTotal: effectivePickUpTotal,
          tableNumber: isParcelQR
            ? (tableParam || "PARCEL")
            : (tableParam && tableParam !== "0" && tableParam !== "PARCEL" ? tableParam : null),
          orderType: dynamicOrderType,
          cookingInstructions: cookingInstructions.trim() || undefined,
        };

        const response = await OrderService.placeOrder(storeId, orderData);

        if (response.status === 201) {
          dispatch(
            showToast({ message: "Order placed successfully!", type: "success" })
          );
          setCartDineIn({});
          setCartPickUp({});
          sessionStorage.removeItem("customerLandingCart");
          if (response?.data?.order) {
            setActiveOrder(response.data.order);
            localStorage.setItem("currentOrder", response.data.order._id);
            localStorage.setItem("currentOrderToken", response.data.order.token || "");
          }
        } else {
          throw new Error(response.data?.message || "Unexpected error");
        }
      }
    } catch (error) {
      console.error("API Error:", error);
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Something went wrong!",
          type: "error",
        })
      );
    } finally {
      dispatch(hideSpinner());
    }
  };

  // Show Active Order Screen if active order exists AND customer is NOT actively editing
  if (activeOrder && !isEditing) {
    return (
      <div className="main-container">
        <div className="header-nav-bar">
          <button className="back-to-menu-btn" onClick={() => navigate("/")}>
            &larr; Home
          </button>
          <span className="header-title">Active Order</span>
        </div>
        <ActiveOrderScreen
          order={activeOrder}
          onOrderUpdated={(updated) => setActiveOrder(updated)}
          onOrderCancelled={() => {
            setActiveOrder(null);
            localStorage.removeItem("currentOrder");
            localStorage.removeItem("currentOrderToken");
            dispatch(showToast({ message: "Order cancelled.", type: "info" }));
          }}
          onEditOrder={handleStartEditOrder}
          onNewOrder={() => {
            setActiveOrder(null);
            setIsEditing(false);
            setCartDineIn({});
            setCartPickUp({});
            localStorage.removeItem("currentOrder");
            localStorage.removeItem("currentOrderToken");
          }}
        />
      </div>
    );
  }

  // Active items for current selected view
  const currentEntries =
    selectedTab === "Dine In" ? dineInCartEntries : pickUpCartEntries;

  return (
    <div className="checkout-page-wrapper">
      <div className="checkout-main-container">
        {/* Upfront Mandatory Mobile Auth Modal */}
        <MobileAuthModal
          isOpen={showMobileModal}
          restaurantName={storeInfo?.name}
          onSubmit={handleMobileAuthSubmit}
        />

        {/* Top Sticky Header */}
        <div className="checkout-header-sticky">
          <div className="header-top-row">
            <button
              className="checkout-back-btn"
              onClick={() => {
                if (isEditing) {
                  setIsEditing(false);
                } else {
                  navigate("/", { state: { activeTab: selectedTab } });
                }
              }}
            >
              &larr; {isEditing ? "Cancel Edit" : "Add Dishes"}
            </button>
            <div className="checkout-title-wrap">
              <h1 className="checkout-page-title">
                {isEditing ? `Editing Order #${activeOrder?.token}` : "Review Order"}
              </h1>
              <span className="table-badge">
                {isParcelQR
                  ? "🛍️ Takeaway"
                  : tableParam && tableParam !== "0"
                  ? `📍 Table ${tableParam}`
                  : "🍽️ Dine-in"}
              </span>
            </div>
          </div>

          {/* Tab Switcher: Dine-In vs Pick-up (if not strictly Parcel QR) */}
          {!isParcelQR && (
            <div className="checkout-mode-tabs">
              <button
                className={`mode-tab-btn ${selectedTab === "Dine In" ? "active" : ""}`}
                onClick={() => setSelectedTab("Dine In")}
              >
                🍽️ Dine In ({dineInCartEntries.reduce((s, [_, i]) => s + i.quantity, 0)})
              </button>
              <button
                className={`mode-tab-btn ${selectedTab === "Pick Up" ? "active" : ""}`}
                onClick={() => setSelectedTab("Pick Up")}
              >
                🛍️ Takeaway ({pickUpCartEntries.reduce((s, [_, i]) => s + i.quantity, 0)})
              </button>
            </div>
          )}
        </div>

        <div className="checkout-content-area">
          {/* Cart Items Card */}
          <div className="checkout-card items-card">
            <div className="card-header-row">
              <h2 className="card-heading">
                {isParcelQR
                  ? "🛍️ Takeaway Dishes"
                  : selectedTab === "Dine In"
                  ? "🍽️ Dine-In Dishes"
                  : "🛍️ Takeaway Dishes"}
              </h2>
              <span className="badge-count">{currentEntries.length} items</span>
            </div>

            {currentEntries.length === 0 ? (
              <div className="empty-cart-state">
                <span className="empty-icon">🛒</span>
                <h3>No items in {isParcelQR ? "Takeaway" : selectedTab === "Pick Up" ? "Takeaway" : selectedTab}</h3>
                <p>Your selected dishes will appear here.</p>
                <button
                  className="btn-browse-menu"
                  onClick={() => navigate("/", { state: { activeTab: selectedTab } })}
                >
                  + Browse Menu & Add Dishes
                </button>
              </div>
            ) : (
              <div className="cart-items-list">
                {currentEntries.map(([itemId, item]) => {
                  const liveDetails = getItemDetails(itemId);
                  const itemName = item.name || liveDetails?.name || "Dish Item";
                  const itemImage = item.image || liveDetails?.image;
                  const unitPrice = item.price || liveDetails?.price || 0;
                  const itemTotal = unitPrice * item.quantity;

                  return (
                    <div key={itemId} className="cart-item-row">
                      <div className="item-meta">
                        {itemImage ? (
                          <img
                            src={itemImage}
                            alt={itemName}
                            className="item-thumb"
                          />
                        ) : (
                          <div className="item-thumb-placeholder">🍛</div>
                        )}
                        <div className="item-text">
                          <div className="item-name">{itemName}</div>
                          <div className="item-unit-price">₹{unitPrice} each</div>

                          {/* Item-level Cooking Note Display & Edit */}
                          {item.notes && editingNoteItemId !== itemId && (
                            <div
                              className="item-note-pill"
                              onClick={() => setEditingNoteItemId(itemId)}
                              title="Click to edit cooking note"
                            >
                              <span>✍️ {item.notes}</span>
                              <span className="item-note-edit-link">Edit</span>
                            </div>
                          )}

                          {!item.notes && editingNoteItemId !== itemId && (
                            <button
                              type="button"
                              className="btn-add-item-note"
                              onClick={() => setEditingNoteItemId(itemId)}
                            >
                              + Add cooking note
                            </button>
                          )}

                          {editingNoteItemId === itemId && (
                            <div className="item-note-inline-editor">
                              <input
                                type="text"
                                className="item-note-input"
                                placeholder="e.g., Less spicy / No onion / Extra crispy"
                                defaultValue={item.notes || ""}
                                autoFocus
                                id={`note-input-${itemId}`}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    updateItemNote(selectedTab, itemId, e.target.value);
                                    setEditingNoteItemId(null);
                                  } else if (e.key === "Escape") {
                                    setEditingNoteItemId(null);
                                  }
                                }}
                              />
                              <div className="item-note-actions">
                                <button
                                  type="button"
                                  className="btn-save-note"
                                  onClick={() => {
                                    const input = document.getElementById(`note-input-${itemId}`);
                                    updateItemNote(selectedTab, itemId, input ? input.value : "");
                                    setEditingNoteItemId(null);
                                  }}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="btn-cancel-note"
                                  onClick={() => setEditingNoteItemId(null)}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="item-actions">
                        <div className="qty-stepper">
                          <button
                            type="button"
                            className="stepper-btn minus"
                            onClick={() => updateCartQuantity(selectedTab, itemId, -1)}
                            title="Decrease"
                          >
                            -
                          </button>
                          <span className="stepper-val">{item.quantity}</span>
                          <button
                            type="button"
                            className="stepper-btn plus"
                            onClick={() => updateCartQuantity(selectedTab, itemId, 1)}
                            title="Increase"
                          >
                            +
                          </button>
                        </div>

                        <div className="item-subtotal">₹{itemTotal}</div>

                        <button
                          type="button"
                          className="btn-remove-item"
                          onClick={() => removeCartItem(selectedTab, itemId)}
                          title="Remove dish"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="add-more-row">
                  <button
                    type="button"
                    className="btn-add-more-dishes"
                    onClick={() => navigate("/", { state: { activeTab: selectedTab } })}
                  >
                    + Add More Dishes from Menu
                  </button>
                </div>
              </div>
            )}
          </div>

        {/* Order / Serving Preferences Box */}
        {totalItemCount > 0 && (
          <div className="checkout-card instructions-card">
            <label className="instructions-label" htmlFor="cooking-notes">
              ✍️ Order / Serving Preferences (Optional)
            </label>
            <textarea
              id="cooking-notes"
              rows={2}
              className="instructions-textarea"
              placeholder="e.g., Serve all items together, extra paper cups, pack chutney separately..."
              value={cookingInstructions}
              onChange={(e) => setCookingInstructions(e.target.value)}
            />
          </div>
        )}

        {/* Customer Mobile Verification Badge */}
        {mobileNumber && (
          <div className="checkout-card customer-badge-card">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <span className="text-muted d-block" style={{ fontSize: "12px" }}>
                  Customer Mobile Number
                </span>
                <strong className="text-dark">📱 +91 {mobileNumber}</strong>
              </div>
              <button
                type="button"
                className="btn-change-mobile"
                onClick={() => setShowMobileModal(true)}
              >
                Change
              </button>
            </div>
          </div>
        )}

        {/* Bill Summary Card */}
        {totalItemCount > 0 && (
          <div className="checkout-card bill-card">
            <h2 className="card-heading mb-3">Bill Summary</h2>
            {!isParcelQR && dineInTotal > 0 && (
              <div className="bill-line">
                <span>Dine-In Total:</span>
                <strong>₹{dineInTotal}</strong>
              </div>
            )}
            {pickUpTotal > 0 && (
              <div className="bill-line">
                <span>{isParcelQR ? "Takeaway Total:" : "Takeaway Total:"}</span>
                <strong>₹{pickUpTotal}</strong>
              </div>
            )}
            <div className="bill-divider" />
            <div className="bill-line grand-total-line">
              <span>Total Payable:</span>
              <span className="grand-price">₹{grandTotal}</span>
            </div>
          </div>
        )}
      </div>

      {/* Floating Bottom Place Order Action Bar */}
      {totalItemCount > 0 && (
        <div className="checkout-bottom-bar">
          <div className="bottom-bar-inner">
            <div className="bottom-total-summary">
              <span className="items-count-text">
                {totalItemCount} {totalItemCount === 1 ? "item" : "items"}
              </span>
              <strong className="bottom-price-text">₹{grandTotal}</strong>
            </div>
            <button
              type="button"
              className="btn-place-order-cta"
              onClick={handleOrder}
            >
              {isEditing ? "SAVE UPDATED ORDER ➔" : "CONFIRM & PLACE ORDER ➔"}
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default PlaceOrder;
